import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import MainLayout from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/seo/SEOHead';
import { NotFoundState } from '@/components/common/NotFoundState';
import { EventWebsiteButton } from '@/components/events/EventWebsiteButton';
import LocalizedLink from '@/i18n/LocalizedLink';
import { useLang } from '@/i18n/useLocalizedPath';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, MapPin, Loader2 } from 'lucide-react';
import QuickShareButton from '@/components/boost/QuickShareButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to check if event is past
const isEventPast = (event) => {
  if (!event.dates?.end) return false;
  const endDate = new Date(event.dates.end);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return endDate < today;
};

// Filter functions
const filterUpcomingEvents = (events) => events.filter(e => !isEventPast(e));
const filterPastEvents = (events) => events.filter(e => isEventPast(e));

export default function VenueEventsPage() {
  const { t } = useTranslation();
  const lang = useLang();
  const { citySlug, venueSlug } = useParams();
  const [venue, setVenue] = useState(null);
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    const fetchVenue = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/api/public/events/${citySlug}/${venueSlug}?lang=${lang}`);
        setVenue(response.data.venue);
        setCity(response.data.venue.city);
      } catch (err) {
        console.error('Error fetching venue:', err);
        if (err.response?.status === 404) {
          setNotFound(true);
        } else {
          setError(t('venue.loadError'));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchVenue();
  }, [citySlug, venueSlug, t, lang]);

  if (notFound) {
    return (
      <MainLayout>
        <NotFoundState
          title={t('venue.notFound')}
          primaryTo="/events"
          primaryLabel={t('notFoundPage.eventsCta')}
        />
      </MainLayout>
    );
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen flex justify-center items-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3 text-gray-600">{t('common.loading')}</span>
        </div>
      </MainLayout>
    );
  }

  if (error || !venue) {
    return (
      <MainLayout>
        <div className="min-h-screen flex justify-center items-center">
          <p className="text-red-600">{error || t('venue.notFound')}</p>
        </div>
      </MainLayout>
    );
  }

  const upcomingEvents = filterUpcomingEvents(venue.events || []);
  const pastEvents = filterPastEvents(venue.events || []);
  const displayEvents = showPast ? pastEvents : upcomingEvents;

  return (
    <MainLayout>
      <SEOHead
        title={`${venue.name} Events | ${city?.name} | Group Accommodation | Jigsaw Conferences`}
        description={`Upcoming events at ${venue.name} in ${city?.name}. Free group accommodation and meeting room coordination for delegates and teams.`}
        url={`https://events.jigsawconferences.co.uk/events/${citySlug}/${venueSlug}`}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-orange-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
              <LocalizedLink to="/events" className="hover:text-orange-600">{t('eventDetail.breadcrumbEvents')}</LocalizedLink>
              <span>/</span>
              <LocalizedLink to={`/events/${citySlug}`} className="hover:text-orange-600">{city?.name}</LocalizedLink>
              <span>/</span>
              <span className="font-medium">{venue.name}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-testid="venue-events-title">
              {t('venue.heroTitle', { venue: venue.name })}
            </h1>
            <p className="text-xl text-gray-600 mb-6">{venue.intro}</p>
            <div className="flex items-center space-x-2 text-gray-700">
              <MapPin className="w-5 h-5 text-orange-600" />
              <span className="font-medium">{city?.name}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-gray-900">
              {showPast
                ? t('venue.statPast', { count: displayEvents.length })
                : t('venue.statUpcoming', { count: displayEvents.length })}
            </h2>
            
            {pastEvents.length > 0 && (
              <Button
                onClick={() => setShowPast(!showPast)}
                variant="outline"
                className="border-gray-300 hover:border-orange-600"
                data-testid="toggle-past-events"
              >
                {showPast ? t('venue.showUpcoming') : t('venue.viewPastEvents', { count: pastEvents.length })}
              </Button>
            )}
          </div>

          {displayEvents.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">
                {showPast ? t('venue.noPast') : t('venue.noUpcoming')}
              </p>
              {pastEvents.length > 0 && !showPast && (
                <Button
                  onClick={() => setShowPast(true)}
                  variant="outline"
                  className="mt-4 border-orange-600 text-orange-600"
                >
                  {t('venue.viewPastEvents', { count: pastEvents.length })}
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {displayEvents.map((event) => (
                <Card key={event.slug} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-lg transition-shadow" data-testid={`event-card-${event.slug}`}>
                  <CardHeader>
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1">
                        <CardTitle className="text-2xl text-gray-900 mb-2">{event.name}</CardTitle>
                        <CardDescription className="flex items-center space-x-2 text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span>{event.dates?.display}</span>
                        </CardDescription>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 items-start">
                        <QuickShareButton 
                          event={{
                            ...event,
                            citySlug: citySlug,
                            venueSlug: venueSlug,
                            city: city?.name,
                            venue: venue?.name
                          }} 
                        />
                        {event.officialUrl && (
                          <EventWebsiteButton
                            event={event}
                            className="border border-gray-300 bg-white text-slate-700 hover:border-orange-600 hover:bg-white w-full sm:w-auto"
                            buttonTestId={`venue-visit-event-website-${event.slug}`}
                          />
                        )}
                        <LocalizedLink to={`/events/${citySlug}/${venueSlug}/${event.slug}`}>
                          <Button className="bg-orange-600 hover:bg-orange-700 text-white w-full sm:w-auto">
                            {t('venue.viewDetails')}
                          </Button>
                        </LocalizedLink>
                      </div>
                      {event.eventWebsiteReview?.publicStatus === 'unverified' && (
                        <p className="text-sm text-amber-700" data-testid={`venue-event-website-status-${event.slug}`}>
                          Event website verification in progress
                        </p>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">{event.shortDescription}</p>
                    <div className="flex flex-wrap gap-2">
                      {(event.sectorTags || []).map((tag) => (
                        <Badge key={tag} variant="secondary" className="bg-orange-100 text-orange-700">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-orange-600 to-orange-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">{t('venue.ctaTitle', { venue: venue.name })}</h2>
          <p className="text-xl mb-8 text-orange-100">
            {t('venue.ctaDesc')}
          </p>
          <Button
            className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-4 text-lg rounded-lg font-medium"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            data-testid="accommodation-cta-button"
          >
            {t('venue.ctaButton')}
          </Button>
        </div>
      </section>
    </MainLayout>
  );
}
